import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import toast from 'react-hot-toast';

const rawSaQuery = fetchBaseQuery({
  baseUrl: '/api/v1/sa',
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('saToken');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    // The SA portal authenticates with a Bearer token from localStorage, but the
    // browser still sends the regular app's `refreshToken` cookie on these
    // same-origin requests. That makes the server's CSRF middleware run, and it
    // then demands a matching x-csrf-token header. Without this, every SA
    // POST/PUT/DELETE 403'd whenever the user also had a normal session open.
    const csrfToken = document.cookie
      .split('; ')
      .find((c) => c.startsWith('csrf-token='))
      ?.split('=')[1];
    if (csrfToken) headers.set('x-csrf-token', csrfToken);

    return headers;
  },
});

const saBaseQuery = async (args, api, extraOptions) => {
  const result = await rawSaQuery(args, api, extraOptions);
  if (result?.error) {
    const status = result.error.status;
    const msg = result.error.data?.error?.message || '';
    if (status === 401) {
      localStorage.removeItem('saToken'); localStorage.removeItem('saUser');
      window.location.href = '/sa/login';
    } else if (status >= 400) {
      toast.error(msg || `Error ${status}`, { duration: 4000 });
    }
  }
  return result;
};

export const superAdminApi = createApi({
  reducerPath: 'superAdminApi',
  baseQuery: saBaseQuery,
  tagTypes: ['SADashboard', 'SASubscribers', 'SAPlans', 'SAAddons', 'SAPayments', 'SAAlerts', 'SAAnalytics', 'SAMe', 'SACoupons', 'SAPromotions', 'SAMeters', 'SAUsage', 'SAEvents', 'SAWallets', 'SATaxRates', 'SAEntitlements', 'SALineItems', 'SAPlatformConfig', 'SAWebhook', 'SAWebhookDelivery'],
  endpoints: (b) => ({
    // Auth
    saLogin: b.mutation({ query: (body) => ({ url: '/auth/login', method: 'POST', body }) }),
    saGetMe: b.query({ query: () => '/me', providesTags: ['SAMe'] }),

    // Dashboard
    saDashboard: b.query({ query: () => '/dashboard', providesTags: ['SADashboard'] }),
    saAnalytics: b.query({ query: () => '/analytics', providesTags: ['SAAnalytics'] }),

    // Subscribers
    saListSubscribers: b.query({ query: (params) => ({ url: '/subscribers', params }), providesTags: ['SASubscribers'] }),
    saGetSubscriber: b.query({ query: (id) => `/subscribers/${id}`, providesTags: (r, e, id) => [{ type: 'SASubscribers', id }] }),
    saUpdateOverrides: b.mutation({ query: ({ workspaceId, ...body }) => ({ url: `/subscribers/${workspaceId}/overrides`, method: 'PUT', body }), invalidatesTags: ['SASubscribers'] }),
    saSuspendSubscriber: b.mutation({ query: (id) => ({ url: `/subscribers/${id}/suspend`, method: 'POST' }), invalidatesTags: ['SASubscribers', 'SADashboard'] }),
    saActivateSubscriber: b.mutation({ query: (id) => ({ url: `/subscribers/${id}/activate`, method: 'POST' }), invalidatesTags: ['SASubscribers', 'SADashboard'] }),
    // SA4 — reconcile workspace billing with source-of-truth subscription
    saResyncSubscriber: b.mutation({ query: (id) => ({ url: `/subscribers/${id}/resync`, method: 'POST' }), invalidatesTags: ['SASubscribers', 'SADashboard'] }),
    // SA3 — impersonation: generate short-lived access token to login-as-user
    saImpersonateWorkspace: b.mutation({ query: (workspaceId) => ({ url: `/impersonate/${workspaceId}`, method: 'POST' }) }),

    // Plans
    saListPlans: b.query({ query: () => '/plans', providesTags: ['SAPlans'] }),
    saCreatePlan: b.mutation({ query: (body) => ({ url: '/plans', method: 'POST', body }), invalidatesTags: ['SAPlans'] }),
    saUpdatePlan: b.mutation({ query: ({ planId, ...body }) => ({ url: `/plans/${planId}`, method: 'PUT', body }), invalidatesTags: ['SAPlans'] }),
    saDeletePlan: b.mutation({ query: (id) => ({ url: `/plans/${id}`, method: 'DELETE' }), invalidatesTags: ['SAPlans'] }),

    // Addons
    saListAddons: b.query({ query: () => '/addons', providesTags: ['SAAddons'] }),
    saListAllAddons: b.query({ query: () => '/addons/all', providesTags: ['SAAddons'] }),
    saCreateAddon: b.mutation({ query: (body) => ({ url: '/addons', method: 'POST', body }), invalidatesTags: ['SAAddons'] }),
    saUpdateAddon: b.mutation({ query: ({ addonId, ...body }) => ({ url: `/addons/${addonId}`, method: 'PUT', body }), invalidatesTags: ['SAAddons'] }),
    saDeleteAddon: b.mutation({ query: (id) => ({ url: `/addons/${id}`, method: 'DELETE' }), invalidatesTags: ['SAAddons'] }),

    // Subscriptions
    saCreateSubscription: b.mutation({ query: (body) => ({ url: '/subscriptions', method: 'POST', body }), invalidatesTags: ['SASubscribers', 'SADashboard'] }),
    saChangePlan: b.mutation({ query: ({ subscriptionId, ...body }) => ({ url: `/subscriptions/${subscriptionId}/change-plan`, method: 'PUT', body }), invalidatesTags: ['SASubscribers', 'SADashboard'] }),
    saCancelSubscription: b.mutation({ query: ({ subscriptionId, reason }) => ({ url: `/subscriptions/${subscriptionId}/cancel`, method: 'POST', body: { reason } }), invalidatesTags: ['SASubscribers', 'SADashboard'] }),
    saAddAddon: b.mutation({ query: ({ subscriptionId, ...body }) => ({ url: `/subscriptions/${subscriptionId}/addons`, method: 'POST', body }), invalidatesTags: ['SASubscribers'] }),
    saRemoveAddon: b.mutation({ query: ({ subscriptionId, addonId }) => ({ url: `/subscriptions/${subscriptionId}/addons`, method: 'DELETE', body: { addonId } }), invalidatesTags: ['SASubscribers'] }),

    // Payments
    saListPayments: b.query({ query: (params) => ({ url: '/payments', params }), providesTags: ['SAPayments'] }),
    saCreatePayment: b.mutation({ query: (body) => ({ url: '/payments', method: 'POST', body }), invalidatesTags: ['SAPayments', 'SADashboard'] }),
    saUpdatePaymentStatus: b.mutation({ query: ({ paymentId, ...body }) => ({ url: `/payments/${paymentId}/status`, method: 'PUT', body }), invalidatesTags: ['SAPayments', 'SADashboard'] }),

    // Alerts
    saListAlerts: b.query({ query: (params) => ({ url: '/alerts', params }), providesTags: ['SAAlerts'] }),
    saAcknowledgeAlert: b.mutation({ query: (id) => ({ url: `/alerts/${id}/acknowledge`, method: 'POST' }), invalidatesTags: ['SAAlerts', 'SADashboard'] }),
    saGenerateAlerts: b.mutation({ query: () => ({ url: '/alerts/generate', method: 'POST' }), invalidatesTags: ['SAAlerts'] }),

    // Coupons
    saListCoupons: b.query({ query: () => '/coupons', providesTags: ['SACoupons'] }),
    saCreateCoupon: b.mutation({ query: (body) => ({ url: '/coupons', method: 'POST', body }), invalidatesTags: ['SACoupons'] }),
    saUpdateCoupon: b.mutation({ query: ({ couponId, ...body }) => ({ url: `/coupons/${couponId}`, method: 'PUT', body }), invalidatesTags: ['SACoupons'] }),
    saDeleteCoupon: b.mutation({ query: (id) => ({ url: `/coupons/${id}`, method: 'DELETE' }), invalidatesTags: ['SACoupons'] }),
    saValidateCoupon: b.mutation({ query: (body) => ({ url: '/coupons/validate', method: 'POST', body }) }),

    // Promotions
    saListPromotions: b.query({ query: () => '/promotions', providesTags: ['SAPromotions'] }),
    saCreatePromotion: b.mutation({ query: (body) => ({ url: '/promotions', method: 'POST', body }), invalidatesTags: ['SAPromotions'] }),
    saUpdatePromotion: b.mutation({ query: ({ promotionId, ...body }) => ({ url: `/promotions/${promotionId}`, method: 'PUT', body }), invalidatesTags: ['SAPromotions'] }),
    saDeletePromotion: b.mutation({ query: (id) => ({ url: `/promotions/${id}`, method: 'DELETE' }), invalidatesTags: ['SAPromotions'] }),

    // Usage Meters
    saListUsageMeters: b.query({ query: () => '/usage-meters', providesTags: ['SAMeters'] }),
    saCreateUsageMeter: b.mutation({ query: (body) => ({ url: '/usage-meters', method: 'POST', body }), invalidatesTags: ['SAMeters'] }),
    saUpdateUsageMeter: b.mutation({ query: ({ meterId, ...body }) => ({ url: `/usage-meters/${meterId}`, method: 'PUT', body }), invalidatesTags: ['SAMeters'] }),
    saDeleteUsageMeter: b.mutation({ query: (id) => ({ url: `/usage-meters/${id}`, method: 'DELETE' }), invalidatesTags: ['SAMeters'] }),

    // Usage Records
    saRecordUsage: b.mutation({ query: (body) => ({ url: '/usage-records', method: 'POST', body }), invalidatesTags: ['SAUsage'] }),
    saListUsageRecords: b.query({ query: (params) => ({ url: '/usage-records', params }), providesTags: ['SAUsage'] }),
    saGetUsageSummary: b.query({ query: (params) => ({ url: '/usage-records/summary', params }), providesTags: ['SAUsage'] }),

    // Subscription Events
    saListSubEvents: b.query({ query: (params) => ({ url: '/subscription-events', params }), providesTags: ['SAEvents'] }),

    // Wallets
    saListWallets: b.query({ query: (params) => ({ url: '/wallets', params }), providesTags: ['SAWallets'] }),
    saGetWallet: b.query({ query: (id) => `/wallets/${id}`, providesTags: (r, e, id) => [{ type: 'SAWallets', id }] }),
    saCreditWallet: b.mutation({ query: ({ workspaceId, ...body }) => ({ url: `/wallets/${workspaceId}/credit`, method: 'POST', body }), invalidatesTags: ['SAWallets', 'SADashboard'] }),
    saDebitWallet: b.mutation({ query: ({ workspaceId, ...body }) => ({ url: `/wallets/${workspaceId}/debit`, method: 'POST', body }), invalidatesTags: ['SAWallets'] }),
    saUpdateWalletSettings: b.mutation({ query: ({ workspaceId, ...body }) => ({ url: `/wallets/${workspaceId}/settings`, method: 'PUT', body }), invalidatesTags: ['SAWallets'] }),

    // Tax Rates
    saListTaxRates: b.query({ query: () => '/tax-rates', providesTags: ['SATaxRates'] }),
    saCreateTaxRate: b.mutation({ query: (body) => ({ url: '/tax-rates', method: 'POST', body }), invalidatesTags: ['SATaxRates'] }),
    saUpdateTaxRate: b.mutation({ query: ({ taxId, ...body }) => ({ url: `/tax-rates/${taxId}`, method: 'PUT', body }), invalidatesTags: ['SATaxRates'] }),
    saDeleteTaxRate: b.mutation({ query: (id) => ({ url: `/tax-rates/${id}`, method: 'DELETE' }), invalidatesTags: ['SATaxRates'] }),

    // Entitlements
    saListEntitlements: b.query({ query: (params) => ({ url: '/entitlements', params }), providesTags: ['SAEntitlements'] }),
    saCreateEntitlement: b.mutation({ query: (body) => ({ url: '/entitlements', method: 'POST', body }), invalidatesTags: ['SAEntitlements'] }),
    saUpdateEntitlement: b.mutation({ query: ({ entId, ...body }) => ({ url: `/entitlements/${entId}`, method: 'PUT', body }), invalidatesTags: ['SAEntitlements'] }),
    saDeleteEntitlement: b.mutation({ query: (id) => ({ url: `/entitlements/${id}`, method: 'DELETE' }), invalidatesTags: ['SAEntitlements'] }),
    saGetWorkspaceEntitlements: b.query({ query: (id) => `/entitlements/workspace/${id}` }),

    // Invoice Line Items
    saGetLineItems: b.query({ query: (invoiceId) => `/invoices/${invoiceId}/line-items`, providesTags: ['SALineItems'] }),
    saAddLineItem: b.mutation({ query: ({ invoiceId, ...body }) => ({ url: `/invoices/${invoiceId}/line-items`, method: 'POST', body }), invalidatesTags: ['SALineItems', 'SAPayments'] }),
    saRemoveLineItem: b.mutation({ query: (id) => ({ url: `/line-items/${id}`, method: 'DELETE' }), invalidatesTags: ['SALineItems', 'SAPayments'] }),

    // Platform Config — Payment Gateways & API Settings
    saGetPlatformConfig: b.query({ query: () => '/platform-config', providesTags: ['SAPlatformConfig'] }),
    saUpdateGatewayConfig: b.mutation({ query: (body) => ({ url: '/platform-config/gateway', method: 'PUT', body }), invalidatesTags: ['SAPlatformConfig'] }),
    saSetActiveGateway: b.mutation({ query: (body) => ({ url: '/platform-config/active-gateway', method: 'PUT', body }), invalidatesTags: ['SAPlatformConfig'] }),
    saTestGatewayConnection: b.mutation({ query: (gateway) => ({ url: `/platform-config/gateway/${gateway}/test`, method: 'POST' }), invalidatesTags: ['SAPlatformConfig'] }),
    saUpdateApiSettings: b.mutation({ query: (body) => ({ url: '/platform-config/api-settings', method: 'PUT', body }), invalidatesTags: ['SAPlatformConfig'] }),

    // ── Trial Management ──
    saGetExpiringTrials: b.query({ query: (params) => ({ url: '/trials/expiring', params }), providesTags: ['SASubscription'] }),
    saExpireTrials: b.mutation({ query: () => ({ url: '/trials/expire', method: 'POST' }), invalidatesTags: ['SASubscription'] }),
    saConvertTrial: b.mutation({ query: ({ subscriptionId, ...body }) => ({ url: `/trials/${subscriptionId}/convert`, method: 'POST', body }), invalidatesTags: ['SASubscription'] }),

    // ── Self-Service Subscription ──
    saGetMySubscription: b.query({ query: (workspaceId) => `/subscriptions/workspace/${workspaceId}`, providesTags: ['SASubscription'] }),
    saListAvailablePlans: b.query({ query: () => '/subscriptions/plans/available', providesTags: ['SAPlan'] }),
    saSelfServiceUpgrade: b.mutation({ query: (body) => ({ url: '/subscriptions/upgrade', method: 'POST', body }), invalidatesTags: ['SASubscription'] }),
    saSelfServiceCancel: b.mutation({ query: (body) => ({ url: '/subscriptions/cancel', method: 'POST', body }), invalidatesTags: ['SASubscription'] }),

    // ── Invoice PDF Data ──
    saGenerateInvoiceData: b.query({ query: (paymentId) => `/invoices/${paymentId}/data`, providesTags: ['SAPayment'] }),

    // ── Bulk Operations ──
    saBulkChangePlan: b.mutation({ query: (body) => ({ url: '/bulk/change-plan', method: 'POST', body }), invalidatesTags: ['SASubscription', 'SAPlan'] }),
    saBulkExtendTrial: b.mutation({ query: (body) => ({ url: '/bulk/extend-trial', method: 'POST', body }), invalidatesTags: ['SASubscription'] }),

    // ── Seed Default Plans ──
    saSeedPlans: b.mutation({ query: () => ({ url: '/seed-plans', method: 'POST' }), invalidatesTags: ['SAPlan', 'SAAddon'] }),

    // ── S8: Webhooks ──
    saListWebhooks: b.query({ query: () => '/webhooks', providesTags: ['SAWebhook'] }),
    saGetWebhook: b.query({ query: (id) => `/webhooks/${id}`, providesTags: ['SAWebhook'] }),
    saCreateWebhook: b.mutation({ query: (body) => ({ url: '/webhooks', method: 'POST', body }), invalidatesTags: ['SAWebhook'] }),
    saUpdateWebhook: b.mutation({ query: ({ id, ...body }) => ({ url: `/webhooks/${id}`, method: 'PUT', body }), invalidatesTags: ['SAWebhook'] }),
    saDeleteWebhook: b.mutation({ query: (id) => ({ url: `/webhooks/${id}`, method: 'DELETE' }), invalidatesTags: ['SAWebhook'] }),
    saRotateWebhookSecret: b.mutation({ query: (id) => ({ url: `/webhooks/${id}/rotate-secret`, method: 'POST' }), invalidatesTags: ['SAWebhook'] }),
    saTestWebhook: b.mutation({ query: (id) => ({ url: `/webhooks/${id}/test`, method: 'POST' }) }),
    saListWebhookDeliveries: b.query({
      query: (params) => ({ url: '/webhook-deliveries', params }),
      providesTags: ['SAWebhookDelivery'],
    }),
    saRetryWebhookDelivery: b.mutation({ query: (id) => ({ url: `/webhook-deliveries/${id}/retry`, method: 'POST' }), invalidatesTags: ['SAWebhookDelivery'] }),
  }),
});

export const {
  useSaLoginMutation, useSaGetMeQuery,
  useSaDashboardQuery, useSaAnalyticsQuery,
  useSaListSubscribersQuery, useSaGetSubscriberQuery, useSaUpdateOverridesMutation,
  useSaSuspendSubscriberMutation, useSaActivateSubscriberMutation, useSaResyncSubscriberMutation, useSaImpersonateWorkspaceMutation,
  useSaListPlansQuery, useSaCreatePlanMutation, useSaUpdatePlanMutation, useSaDeletePlanMutation,
  useSaListAddonsQuery, useSaListAllAddonsQuery, useSaCreateAddonMutation, useSaUpdateAddonMutation, useSaDeleteAddonMutation,
  useSaCreateSubscriptionMutation, useSaChangePlanMutation, useSaCancelSubscriptionMutation,
  useSaAddAddonMutation, useSaRemoveAddonMutation,
  useSaListPaymentsQuery, useSaCreatePaymentMutation, useSaUpdatePaymentStatusMutation,
  useSaListAlertsQuery, useSaAcknowledgeAlertMutation, useSaGenerateAlertsMutation,
  useSaListCouponsQuery, useSaCreateCouponMutation, useSaUpdateCouponMutation, useSaDeleteCouponMutation, useSaValidateCouponMutation,
  useSaListPromotionsQuery, useSaCreatePromotionMutation, useSaUpdatePromotionMutation, useSaDeletePromotionMutation,
  useSaListUsageMetersQuery, useSaCreateUsageMeterMutation, useSaUpdateUsageMeterMutation, useSaDeleteUsageMeterMutation,
  useSaRecordUsageMutation, useSaListUsageRecordsQuery, useSaGetUsageSummaryQuery,
  useSaListSubEventsQuery,
  useSaListWalletsQuery, useSaGetWalletQuery, useSaCreditWalletMutation, useSaDebitWalletMutation, useSaUpdateWalletSettingsMutation,
  useSaListTaxRatesQuery, useSaCreateTaxRateMutation, useSaUpdateTaxRateMutation, useSaDeleteTaxRateMutation,
  useSaListEntitlementsQuery, useSaCreateEntitlementMutation, useSaUpdateEntitlementMutation, useSaDeleteEntitlementMutation, useSaGetWorkspaceEntitlementsQuery,
  useSaGetLineItemsQuery, useSaAddLineItemMutation, useSaRemoveLineItemMutation,
  useSaGetPlatformConfigQuery, useSaUpdateGatewayConfigMutation, useSaSetActiveGatewayMutation,
  useSaTestGatewayConnectionMutation, useSaUpdateApiSettingsMutation,
  // Trial & Subscription
  useSaGetExpiringTrialsQuery, useSaExpireTrialsMutation, useSaConvertTrialMutation,
  useSaGetMySubscriptionQuery, useSaListAvailablePlansQuery,
  useSaSelfServiceUpgradeMutation, useSaSelfServiceCancelMutation,
  useSaGenerateInvoiceDataQuery,
  useSaBulkChangePlanMutation, useSaBulkExtendTrialMutation, useSaSeedPlansMutation,
  // S8 — Webhooks
  useSaListWebhooksQuery, useSaGetWebhookQuery, useSaCreateWebhookMutation,
  useSaUpdateWebhookMutation, useSaDeleteWebhookMutation, useSaRotateWebhookSecretMutation,
  useSaTestWebhookMutation, useSaListWebhookDeliveriesQuery, useSaRetryWebhookDeliveryMutation,
} = superAdminApi;
